import { useEffect } from 'react';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import Widget from 'components/Dashboard/Widget';
import { useKPIGetInternalDeliveryTransportMix } from 'api/kpi/kpi';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useManagementKpiFilter from './useManagementKpiFilter';
import {
  colorAt,
  nf,
  pct,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from './shared';

/**
 * P3 — annonsbilar som gick med lastbil kontra med fälttestare.
 *
 * Tre lägen redovisas, inte två: egen biltransport och inhyrd transportör är olika
 * kostnadsbilder. Lastbilstotalen är summan av de två första.
 */
const InternalDeliveryTransportWidget = () => {
  const { isReady, isDateRangeTooLong, filter, deps } = useManagementKpiFilter();
  const getData = useKPIGetInternalDeliveryTransportMix();

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const result = getData.data;

  const byMode = new Map<string, number>();
  (result?.dataPoints ?? []).forEach((dp) => {
    byMode.set(dp.modeName, (byMode.get(dp.modeName) ?? 0) + dp.cars);
  });
  const modes = Array.from(byMode.entries())
    .map(([name, cars]) => ({ name, cars }))
    .sort((a, b) => b.cars - a.cars);

  const total = result?.totalCars ?? 0;

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.InternalDeliveryTransport]}
      count={total}
      isLoading={getData.isLoading}
      info="Fördelningen av annonsbilar på lastbil kontra fälttestare. Räknas per ärende, så totalen stämmer mot annonsraden i Levererade bilar. Har bilen legat på en biltransport någon del av vägen räknas den som lastbilstransport."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={modes.length === 0}
      >
        <p style={{ textAlign: 'center', marginBottom: '10px' }}>
          Lastbil <strong>{pct(result?.truckPercentage)}</strong> · Fälttestare{' '}
          <strong>{pct(result?.fieldTesterPercentage)}</strong>
        </p>

        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={modes} dataKey="cars" nameKey="name" cx="50%" cy="50%" outerRadius={85}>
                {modes.map((_, index) => (
                  <Cell key={index} fill={colorAt(index)} />
                ))}
              </Pie>
              <Tooltip formatter={(v: number, name: string) => [`${nf(v)} bilar`, name]} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Transportslag</th>
              <th style={thNumStyle}>Antal bilar</th>
              <th style={thNumStyle}>Andel</th>
            </tr>
          </thead>
          <tbody>
            {modes.map((mode) => (
              <tr key={mode.name} style={{ borderBottom: '1px solid #eee' }}>
                <td style={tdStyle}>{mode.name}</td>
                <td style={tdNumStyle}>{nf(mode.cars)}</td>
                <td style={tdNumStyle}>
                  {total > 0 ? `${nf((mode.cars / total) * 100, 1)} %` : '–'}
                </td>
              </tr>
            ))}
            <tr style={totalRowStyle}>
              <td style={tdStyle}>Totalt</td>
              <td style={tdNumStyle}>{nf(total)}</td>
              <td style={tdNumStyle}>100 %</td>
            </tr>
          </tbody>
        </table>
      </WidgetState>
    </Widget>
  );
};

export default InternalDeliveryTransportWidget;
